import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { siteConfig, stats, testimonials } from '@/mocks/navigation';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string)?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qaadue3oq4jqumtj80', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteConfig.name}`,
    description: `${siteConfig.name}品牌介绍、用户口碑与联系方式`,
    url: `${siteConfig.url}/about`,
    inLanguage: 'zh-CN',
    about: {
      '@type': 'Organization',
      name: siteConfig.name,
      url: siteConfig.url,
      description: siteConfig.description,
      foundingDate: siteConfig.foundingYear,
      areaServed: {
        '@type': 'Country',
        name: '中国',
      },
    },
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`关于我们 | ${siteConfig.name}`}
        description={`了解${siteConfig.name}的品牌故事、核心团队、用户口碑与联系方式。${siteConfig.description}`}
        keywords="夜色导航关于我们,夜间导航品牌,夜色导航联系方式,导航软件品牌介绍"
        canonicalUrl={`${siteConfig.url}/about`}
        schema={aboutSchema}
      />

      <div className="min-h-screen bg-background-50 text-foreground-500">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />

            {/* Hero Section */}
            <section className="py-12 md:py-20">
              <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
                <div className="flex-1">
                  <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-6">
                    关于<span className="text-primary-500">夜色导航</span>
                  </h1>
                  <p className="text-foreground-400 text-sm md:text-base leading-relaxed mb-6">
                    {siteConfig.name}成立于{siteConfig.foundingYear}年，是一支专注于夜间出行场景的科技创新团队。
                    我们深刻理解夜间出行的特殊需求与挑战，致力于通过技术创新让每一次夜间出行更加安全、便捷。
                  </p>
                  <p className="text-foreground-400 text-sm md:text-base leading-relaxed mb-6">
                    从最初的夜间地图渲染引擎研发，到如今涵盖夜间驾车、户外探险和城市夜行三大场景的全方位导航服务平台，
                    我们始终坚持一个核心理念：<strong className="text-foreground-700">黑暗不应该成为出行的障碍，而应该是探索世界的全新维度。</strong>
                  </p>
                  <p className="text-foreground-400 text-sm md:text-base leading-relaxed">
                    截至目前，夜色导航已累计服务超过500万用户，覆盖全国300多个城市。
                    我们的团队汇聚了来自地图导航、人工智能、视觉设计和用户体验等领域的专业人才，
                    持续为夜间出行者提供最可靠的方向指引。
                  </p>
                </div>

                <div className="w-full lg:w-[420px] flex-shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20tech%20office%20at%20night%20with%20large%20screen%20displaying%20navigation%20map%2C%20warm%20ambient%20lighting%2C%20minimalist%20workspace%2C%20dark%20environment%20with%20city%20lights%20through%20window%2C%20professional%20atmosphere&width=800&height=600&seq=about-hero-01&orientation=portrait"
                    alt="夜色导航团队"
                    className="w-full h-72 md:h-80 object-cover rounded-xl"
                  />
                </div>
              </div>
            </section>

            {/* Stats */}
            <section className="py-12 md:py-16 border-t border-background-200/50">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1.5">
                      {stat.value}
                    </div>
                    <div className="text-foreground-400 text-xs">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Brand Values */}
            <section className="py-12 md:py-16 border-t border-background-200/50">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-10 text-center">
                我们的<span className="text-accent-500">核心价值观</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    icon: 'ri-shield-check-line',
                    title: '安全至上',
                    desc: '每一次路线推荐、每一个预警功能，都以用户安全为首要目标。我们持续优化算法，确保夜间出行的每一个环节都有可靠保障。',
                  },
                  {
                    icon: 'ri-lightbulb-line',
                    title: '技术创新',
                    desc: '从低蓝光显示到AI路况预测，从惯性导航到天文定位，我们不断探索技术边界，为用户提供最前沿的夜间导航体验。',
                  },
                  {
                    icon: 'ri-user-heart-line',
                    title: '用户为本',
                    desc: '深入理解夜间出行者的真实痛点，以用户需求驱动产品迭代。每一处设计细节都源于对用户场景的深刻洞察。',
                  },
                ].map((value) => (
                  <div
                    key={value.title}
                    className="bg-background-100 border border-background-200/50 rounded-xl p-6 text-center"
                  >
                    <div className="w-14 h-14 mx-auto flex items-center justify-center bg-accent-500/15 rounded-xl mb-5">
                      <i className={`${value.icon} text-accent-500 text-2xl`}></i>
                    </div>
                    <h3 className="text-foreground-700 font-semibold text-base mb-3">{value.title}</h3>
                    <p className="text-foreground-400 text-sm leading-relaxed">{value.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Testimonials */}
            <section className="py-12 md:py-16 border-t border-background-200/50">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-10 text-center">
                用户<span className="text-primary-500">口碑</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {testimonials.map((t) => (
                  <div
                    key={t.id}
                    className="bg-background-100 border border-background-200/50 rounded-xl p-6"
                  >
                    <div className="flex items-center gap-1 mb-4">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${
                            i < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-fill text-foreground-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-foreground-500 text-sm leading-relaxed mb-5">{t.content}</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-secondary-500/20 flex items-center justify-center text-secondary-600 font-semibold text-sm">
                        {t.name.charAt(0)}
                      </div>
                      <div>
                        <div className="text-foreground-700 text-sm font-medium">{t.name}</div>
                        <div className="text-foreground-400 text-xs">{t.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Section */}
            <section className="py-12 md:py-16 border-t border-background-200/50">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-10 text-center">
                联系<span className="text-accent-500">我们</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-4xl mx-auto">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-primary-500/15 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-foreground-700 font-medium text-sm mb-1">电子邮箱</h3>
                      <p className="text-foreground-400 text-sm">contact@yesedaohang.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-accent-500/15 rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-accent-500 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-foreground-700 font-medium text-sm mb-1">客服电话</h3>
                      <p className="text-foreground-400 text-sm">400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-secondary-500/15 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-secondary-500 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-foreground-700 font-medium text-sm mb-1">公司地址</h3>
                      <p className="text-foreground-400 text-sm">北京市海淀区中关村科技园区创新大厦18层</p>
                    </div>
                  </div>
                </div>

                <form
                  className="space-y-4"
                  data-readdy-form=""
                  onSubmit={handleSubmit}
                >
                  <input
                    type="text"
                    name="phone_alt"
                    className="hp-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <div>
                    <label htmlFor="name" className="block text-foreground-500 text-sm mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full bg-background-100 border border-background-200/50 rounded-lg px-4 py-2.5 text-sm text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50 transition-colors duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-foreground-500 text-sm mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full bg-background-100 border border-background-200/50 rounded-lg px-4 py-2.5 text-sm text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50 transition-colors duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-foreground-500 text-sm mb-1.5">
                      留言
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full bg-background-100 border border-background-200/50 rounded-lg px-4 py-2.5 text-sm text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50 transition-colors duration-200 resize-none"
                      placeholder="请输入您的留言（500字以内）"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold px-6 py-3 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
                  >
                    发送留言
                  </button>

                  {formStatus === 'success' && (
                    <div className="bg-accent-500/10 border border-accent-500/20 text-accent-600 text-sm rounded-lg px-4 py-3 mt-4">
                      <i className="ri-check-line mr-1.5"></i>
                      感谢您的留言，我们会尽快回复！
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg px-4 py-3 mt-4">
                      <i className="ri-error-warning-line mr-1.5"></i>
                      {formError || '提交失败，请稍后重试'}
                    </div>
                  )}
                </form>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}